import { MILESTONE_ITEMS_PER_PAGE, type MilestoneBody, type MilestoneRow, type MilestonesResponse } from '@pulse/contracts';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { AsOfPill } from '@/components/pulse/day';
import { BackIcon } from '@/components/pulse/icons';
import { PageHeader } from '@/components/pulse/page-header';
import { SourceLink } from '@/components/pulse/source-link';
import { useChangeMilestones, useMilestoneCandidates, useMilestones, type MilestoneChange } from '@/domains/projects/api';
import { ProjectTabs } from '@/domains/projects/project-tab';
import { useShell } from '@/domains/shell/api';
import { ApiRequestError } from '@/lib/api-client';

/** The prototype's CHIP colours for milestone and item states. */
const CHIP: Readonly<Record<string, string>> = {
  'On track': 'c-merged',
  'At risk': 'c-warn',
  Late: 'c-changes',
  Done: 'c-approved',
  Closed: 'c-kind',
  Accepted: 'c-approved',
  Completed: 'c-approved',
  'In progress': 'c-review',
  'In review': 'c-review',
  Blocked: 'c-changes',
};
export const Chip = ({ state }: { readonly state: string }) => <span className={`chip ${CHIP[state] ?? 'c-kind'}`}>{state}</span>;
/** The meter's colour for a state, as the prototype's `msTone`. */
const TONE: Readonly<Record<string, string>> = { 'At risk': '--warn-ink', Late: '--bad-ink', Done: '--s-acc', Closed: '--muted-2' };
const inputStyle: React.CSSProperties = { height: 40, padding: '0 12px', border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface-2)', color: 'var(--ink)' };

export function Meter({ done, total, state }: { readonly done: number; readonly total: number; readonly state: string }): React.ReactElement {
  return (
    <div className="meter" role="img" aria-label={`${done} of ${total} done`}>
      <i style={{ width: `${total === 0 ? 0 : Math.round((done / total) * 100)}%`, background: `var(${TONE[state] ?? '--accent'})` }} />
    </div>
  );
}

/** Projects › Milestones (MS-1 to MS-5), built from the prototype's `viewMilestones()`. As of now. */
export function ProjectMilestonesPage(): React.ReactElement {
  const { productSlug = '' } = useParams();
  const page = useMilestones(productSlug);
  const shell = useShell();
  const change = useChangeMilestones(productSlug);
  const [editing, setEditing] = React.useState<{ readonly id: string | null } | null>(null);
  const error = change.error instanceof ApiRequestError ? change.error.toUserMessage() : change.error instanceof Error ? change.error.message : null;
  return (
    <>
      <PageHeader
        eyebrow="Project · milestones"
        title={page.data?.product.name ?? 'Milestones'}
        sub="What the project is working toward. Talos fills each milestone and forecasts it from the project's pace."
        right={
          <>
            <button type="button" className="btn primary" onClick={() => setEditing({ id: null })}>
              New milestone
            </button>
            <AsOfPill label={shell.data?.freshness.asOfLabel ?? null} />
          </>
        }
      />
      {page.isError ? <span className="sub">There is no project by that name.</span> : null}
      {page.data !== undefined ? (
        <MilestonesView
          data={page.data}
          editing={editing}
          onEditing={setEditing}
          busy={change.isPending}
          error={error}
          onChange={(next) => change.mutate(next, { onSuccess: () => (next.kind === 'create' || next.kind === 'edit' ? setEditing(null) : undefined) })}
          productSlug={productSlug}
        />
      ) : null}
    </>
  );
}

export function MilestonesView({
  data,
  editing,
  onEditing,
  onChange,
  busy,
  error,
  productSlug,
}: {
  readonly data: MilestonesResponse;
  readonly editing: { readonly id: string | null } | null;
  readonly onEditing: (editing: { readonly id: string | null } | null) => void;
  readonly onChange: (change: MilestoneChange) => void;
  readonly busy: boolean;
  readonly error: string | null;
  readonly productSlug: string;
}): React.ReactElement {
  const open = data.milestones.filter((milestone) => milestone.state !== 'Closed');
  const closed = data.milestones.filter((milestone) => milestone.state === 'Closed');
  const editingMilestone = editing?.id === null ? null : (data.milestones.find((milestone) => milestone.id === editing?.id) ?? null);
  return (
    <>
      <ProjectTabs slug={data.product.slug} hasBoard={data.product.hasBoard} hasStories={data.product.hasStories} view="milestones" />
      {editing !== null ? (
        <MilestoneForm
          key={editing.id ?? 'new'}
          milestone={editingMilestone}
          busy={busy}
          error={error}
          onCancel={() => onEditing(null)}
          onSave={(body) => onChange(editingMilestone === null ? { kind: 'create', body } : { kind: 'edit', milestoneId: editingMilestone.id, body })}
        />
      ) : error !== null ? (
        <span role="alert" style={{ fontSize: 13, color: 'var(--bad-ink)' }}>
          {error}
        </span>
      ) : null}
      <div className="two" style={{ gridTemplateColumns: 'minmax(0,1fr) 320px', alignItems: 'start' }}>
        <div className="stack" style={{ gap: 16 }}>
          {open.length === 0 ? (
            <section className="card pad">
              <span style={{ color: 'var(--muted)' }}>No open milestone. Add one to plan against.</span>
            </section>
          ) : null}
          {open.map((milestone) => (
            <MilestoneCard key={milestone.id} milestone={milestone} onChange={onChange} onEdit={() => onEditing({ id: milestone.id })} productSlug={productSlug} />
          ))}
          {closed.length > 0 ? <span className="eyebrow">Closed</span> : null}
          {closed.map((milestone) => (
            <MilestoneCard key={milestone.id} milestone={milestone} onChange={onChange} onEdit={() => onEditing({ id: milestone.id })} productSlug={productSlug} />
          ))}
        </div>
        <aside className="stack" style={{ gap: 16 }}>
          <section className="card pad stack" style={{ gap: 8 }}>
            <h2>How the forecast works</h2>
            <span style={{ color: 'var(--ink-2)' }}>
              The forecast is today plus the work remaining at the project&apos;s pace over the last four weeks
              {data.ratePerWeek > 0 ? `, now ${formatRate(data.ratePerWeek)} a week` : ''}. A milestone is at risk when the forecast is after its date, and late when its date has
              passed with work left.
            </span>
          </section>
          <section className="card pad stack" style={{ gap: 8 }}>
            <h2>Who assigns items</h2>
            <span style={{ color: 'var(--ink-2)' }}>Talos picks each milestone&apos;s stories and tasks. Anyone can add or remove one, and Talos leaves that change alone.</span>
          </section>
        </aside>
      </div>
    </>
  );
}

function formatRate(rate: number): string {
  return Number.isInteger(rate) ? String(rate) : rate.toFixed(1);
}

export function MilestoneForm({
  milestone,
  busy,
  error,
  onSave,
  onCancel,
  projects,
}: {
  readonly milestone: MilestoneRow | null;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onSave: (body: MilestoneBody, project: string) => void;
  readonly onCancel: () => void;
  /** MS-6: from the Milestones page, the projects to pick from; the first is the default. */
  readonly projects?: ReadonlyArray<{ readonly slug: string; readonly name: string }>;
}): React.ReactElement {
  const [project, setProject] = React.useState(projects?.[0]?.slug ?? '');
  const [title, setTitle] = React.useState(milestone?.title ?? '');
  const [description, setDescription] = React.useState(milestone?.description ?? '');
  const [date, setDate] = React.useState(milestone?.date ?? '');
  return (
    <section className="card pad stack" style={{ gap: 12 }} aria-labelledby="msf">
      <h2 id="msf">{milestone === null ? 'New milestone' : 'Edit milestone'}</h2>
      {projects !== undefined ? (
        <label className="stack" style={{ gap: 4, maxWidth: 320 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>Project</span>
          <select value={project} onChange={(event) => setProject(event.target.value)} style={inputStyle}>
            {projects.map((option) => (
              <option key={option.slug} value={option.slug}>
                {option.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <label className="stack" style={{ gap: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>Title</span>
        <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={120} style={inputStyle} />
      </label>
      <label className="stack" style={{ gap: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>Description</span>
        <textarea rows={3} value={description} onChange={(event) => setDescription(event.target.value)} maxLength={2000} />
      </label>
      <label className="stack" style={{ gap: 4, maxWidth: 220 }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>Date</span>
        <input type="date" value={date} onChange={(event) => setDate(event.target.value)} style={inputStyle} />
      </label>
      <span style={{ fontSize: 13, color: 'var(--muted)' }}>Talos fills a new milestone with the project&apos;s stories and tasks on its next pass. You can change what it picks.</span>
      {error !== null ? (
        <span role="alert" style={{ fontSize: 13, color: 'var(--bad-ink)' }}>
          {error}
        </span>
      ) : null}
      <div className="row" style={{ gap: 8 }}>
        <button type="button" className="btn primary" disabled={busy} onClick={() => onSave({ title, description, date }, project)}>
          Save milestone
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </section>
  );
}

export function MilestoneCard({
  milestone,
  onChange,
  onEdit,
  productSlug,
  initialPage = 0,
}: {
  readonly milestone: MilestoneRow;
  readonly onChange: (change: MilestoneChange) => void;
  readonly onEdit: () => void;
  readonly productSlug: string;
  readonly initialPage?: number;
}): React.ReactElement {
  const [page, setPage] = React.useState(initialPage);
  const [confirming, setConfirming] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const pages = Math.max(1, Math.ceil(milestone.items.length / MILESTONE_ITEMS_PER_PAGE));
  const current = Math.min(page, pages - 1);
  const shown = milestone.items.slice(current * MILESTONE_ITEMS_PER_PAGE, (current + 1) * MILESTONE_ITEMS_PER_PAGE);
  const left = milestone.items.filter((item) => !item.done).length;
  const isClosed = milestone.state === 'Closed';
  const behind = milestone.state === 'At risk' || milestone.state === 'Late';
  return (
    <section className="card pad stack" style={{ gap: 12 }}>
      <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div className="stack" style={{ gap: 2 }}>
          <h2>{milestone.title}</h2>
          {milestone.description !== '' ? <span style={{ color: 'var(--ink-2)' }}>{milestone.description}</span> : null}
        </div>
        <span className="row" style={{ gap: 6 }}>
          <Chip state={milestone.source} />
          <Chip state={milestone.state} />
        </span>
      </div>
      <div className="row num" style={{ gap: 24, flexWrap: 'wrap' }}>
        <span className="stack">
          <b style={{ fontSize: 20 }}>{milestone.dateLabel}</b>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>due</span>
        </span>
        <span className="stack">
          <b style={{ fontSize: 20, color: behind ? 'var(--warn-ink)' : 'var(--ink)' }}>{milestone.forecastLabel}</b>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>forecast</span>
        </span>
        <span className="stack">
          <b style={{ fontSize: 20 }}>
            {milestone.done} / {milestone.total}
          </b>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>done</span>
        </span>
      </div>
      <Meter done={milestone.done} total={milestone.total} state={milestone.state} />
      {milestone.talosLine !== null ? (
        <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
          <b style={{ fontWeight: 600 }}>Talos:</b> {milestone.talosLine}
        </span>
      ) : null}
      {milestone.items.length > 0 ? (
        <div className="stack">
          <div className="row" style={{ justifyContent: 'space-between', gap: 8, fontSize: 13, color: 'var(--muted)' }}>
            <span>
              {left} still to do, then {milestone.items.length - left} done
            </span>
            {pages > 1 ? (
              <span className="row" style={{ gap: 6 }} role="group" aria-label={`Items in ${milestone.title}`}>
                <span className="num">
                  {current * MILESTONE_ITEMS_PER_PAGE + 1}–{current * MILESTONE_ITEMS_PER_PAGE + shown.length} of {milestone.items.length}
                </span>
                <button type="button" className="btn" style={{ height: 30, padding: '0 8px' }} aria-label="Previous items" disabled={current === 0} onClick={() => setPage(current - 1)}>
                  <BackIcon />
                </button>
                <button type="button" className="btn" style={{ height: 30, padding: '0 8px' }} aria-label="More items" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>
                  <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
                    <BackIcon />
                  </span>
                </button>
              </span>
            ) : null}
          </div>
          {shown.map((item) => (
            <div key={item.key} className="prow" style={{ gridTemplateColumns: '96px minmax(0,1fr) auto' }}>
              <SourceLink className="mono" href={item.url} style={{ fontSize: 13, fontWeight: 500, textDecoration: 'none' }}>
                {item.ref}
              </SourceLink>
              <span className="stack" style={{ gap: 2 }}>
                <span>{item.title}</span>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                  {[item.who || 'Nobody assigned', `assigned by ${item.assignedBy}${item.assignedOn === '' ? '' : ` · ${item.assignedOn}`}`].join(' · ')}
                </span>
              </span>
              <span className="row" style={{ gap: 6 }}>
                <Chip state={item.state} />
                {milestone.editable && !isClosed ? (
                  <button
                    type="button"
                    className="btn"
                    style={{ height: 28, padding: '0 10px', fontSize: 12 }}
                    aria-label={`Remove ${item.ref} from ${milestone.title}`}
                    onClick={() => onChange({ kind: 'remove-item', milestoneId: milestone.id, key: item.key })}
                  >
                    Remove
                  </button>
                ) : null}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>No items yet{milestone.editable ? '; Talos assigns them on its next pass' : ''}.</span>
      )}
      {adding ? <AddItem productSlug={productSlug} milestone={milestone} onAdd={(key) => onChange({ kind: 'add-item', milestoneId: milestone.id, key })} onDone={() => setAdding(false)} /> : null}
      {confirming ? (
        <div className="confirm" role="alertdialog" aria-labelledby={`md-${milestone.id}`}>
          <strong id={`md-${milestone.id}`}>Delete {milestone.title}?</strong>
          <span>Its items go back to unassigned. The deletion is kept with your name.</span>
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn danger" onClick={() => onChange({ kind: 'delete', milestoneId: milestone.id })}>
              Delete milestone
            </button>
            <button type="button" className="btn" onClick={() => setConfirming(false)}>
              Keep it
            </button>
          </div>
        </div>
      ) : null}
      {milestone.editable ? (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {isClosed ? (
            <button type="button" className="btn" onClick={() => onChange({ kind: 'reopen', milestoneId: milestone.id })}>
              Reopen
            </button>
          ) : (
            <>
              <button type="button" className="btn" onClick={onEdit}>
                Edit
              </button>
              <button type="button" className="btn" onClick={() => setAdding(true)}>
                Add an item
              </button>
              <button type="button" className="btn" onClick={() => onChange({ kind: 'close', milestoneId: milestone.id })}>
                Close
              </button>
            </>
          )}
          <button type="button" className="btn" style={{ color: 'var(--bad-ink)' }} onClick={() => setConfirming(true)}>
            Delete
          </button>
          {milestone.lastChange !== null ? <span style={{ fontSize: 12, color: 'var(--muted)', alignSelf: 'center' }}>{milestone.lastChange}</span> : null}
        </div>
      ) : (
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>From Teamwork: its title, date and tasks follow Teamwork.</span>
      )}
    </section>
  );
}

/** Search the project's stories, tasks and issues and add one (MS-3). */
function AddItem({
  productSlug,
  milestone,
  onAdd,
  onDone,
}: {
  readonly productSlug: string;
  readonly milestone: MilestoneRow;
  readonly onAdd: (key: string) => void;
  readonly onDone: () => void;
}): React.ReactElement {
  const [query, setQuery] = React.useState('');
  const candidates = useMilestoneCandidates(productSlug, milestone.id, query);
  return (
    <div className="stack" style={{ gap: 8, padding: 12, borderRadius: 10, background: 'var(--surface-2)' }}>
      <label className="stack" style={{ gap: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>Find a story, task or issue</span>
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="An ID or words from its title" style={{ ...inputStyle, background: 'var(--surface)' }} />
      </label>
      {(candidates.data?.items ?? []).map((item) => (
        <div key={item.key} className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
          <span>
            <span className="mono" style={{ fontSize: 12, color: 'var(--accent-ink)' }}>
              {item.ref}
            </span>{' '}
            {item.title}
            {item.milestone !== null ? <span style={{ fontSize: 12, color: 'var(--muted)' }}> · now on {item.milestone}</span> : null}
          </span>
          <button type="button" className="btn" style={{ height: 28, padding: '0 10px', fontSize: 12 }} onClick={() => onAdd(item.key)}>
            {item.milestone !== null ? 'Move here' : 'Add'}
          </button>
        </div>
      ))}
      {candidates.data !== undefined && candidates.data.items.length === 0 ? <span style={{ fontSize: 13, color: 'var(--muted)' }}>Nothing matches.</span> : null}
      <button type="button" className="btn" style={{ alignSelf: 'flex-start' }} onClick={onDone}>
        Done
      </button>
    </div>
  );
}
